import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Contract, Customer, Loan } from "@dfp/api-client";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { api } from "../lib/api.js";

export function ContractsPage(): JSX.Element {
  const contracts = useQuery({
    queryKey: ["contracts", "list"],
    queryFn: () => api.get<readonly Contract[]>("/api/contracts"),
  });

  // Customer name isn't on the contracts list DTO (the contracts API is
  // out of scope for a server-side join here), so resolve it client-side
  // the same way DeviceDetailPage does: contract.loanId → loan.customerId
  // → customer name. Both lists are small at MVP volume + already
  // country-scoped server-side. Each query is independent so a failure
  // just leaves the name column as "—" rather than blanking the page.
  const loans = useQuery({
    queryKey: ["loans", "list"],
    queryFn: () => api.get<readonly Loan[]>("/api/loans"),
  });
  const customers = useQuery({
    queryKey: ["customers", "list"],
    queryFn: () => api.get<readonly Customer[]>("/api/customers"),
  });

  // loanId → "Given Family". Built once per data change.
  const customerNameByLoanId = useMemo(() => {
    const customerById = new Map((customers.data ?? []).map((c) => [c.id, c]));
    const byLoan = new Map<string, string>();
    for (const loan of loans.data ?? []) {
      const customer = customerById.get(loan.customerId);
      if (customer) byLoan.set(loan.id, `${customer.givenName} ${customer.familyName}`);
    }
    return byLoan;
  }, [loans.data, customers.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Contracts</h1>
        <p className="text-sm text-slate-500">
          Signed contracts are immutable. Each row is a hash-locked snapshot.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All contracts</CardTitle>
          <span className="text-xs text-slate-500">{contracts.data?.length ?? 0} rows</span>
        </CardHeader>
        <CardBody className="p-0">
          {contracts.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : contracts.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load contracts.</p>
          ) : (contracts.data ?? []).length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No contracts yet.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>ID</Th>
                  <Th>Customer</Th>
                  <Th>Loan</Th>
                  <Th>Template / version</Th>
                  <Th>Language</Th>
                  <Th>Signed</Th>
                  <Th>Hash</Th>
                </tr>
              </THead>
              <TBody>
                {contracts.data?.map((c) => (
                  <Tr key={c.id}>
                    <Td>
                      <Link
                        to="/contracts/$id"
                        params={{ id: c.id }}
                        className="font-mono text-xs text-brand-600 hover:underline"
                      >
                        {c.id.slice(0, 8)}…
                      </Link>
                    </Td>
                    <Td className="text-sm text-slate-700">
                      {customerNameByLoanId.get(c.loanId) ?? (
                        <span className="text-slate-400">
                          {loans.isLoading || customers.isLoading ? "…" : "—"}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <Link
                        to="/loans/$id"
                        params={{ id: c.loanId }}
                        className="font-mono text-xs text-brand-600 hover:underline"
                      >
                        {c.loanId.slice(0, 8)}…
                      </Link>
                    </Td>
                    <Td>
                      {c.templateId} · v{c.version}
                    </Td>
                    <Td>{c.language}</Td>
                    <Td className="text-xs text-slate-500">
                      {new Date(c.signedAt).toLocaleString()}
                    </Td>
                    <Td className="font-mono text-xs">{c.jsonHash.slice(0, 12)}…</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
